/**
 * The table's icons: actions, demands, buildings, commodities and the kinds of
 * Trajan and bonus tile. Original shapes, drawn from primitives — nothing here
 * imitates the published artwork.
 *
 * Every icon is drawn in a 24-unit box centred on the origin, in one colour, so
 * a caller sizes it with `size` and colours it by passing the ink it wants.
 */
import { Graphics, GraphicsPath } from 'pixi.js';

type Draw = (g: Graphics, ink: number) => void;

/** A leaf or grain: a pointed oval from (x, y) along `angle`, `len` long. */
function leaf(g: Graphics, x: number, y: number, len: number, width: number, angle: number) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const at = (a: number, b: number): [number, number] => [
    x + a * cos - b * sin,
    y + a * sin + b * cos,
  ];
  const [tx, ty] = at(len, 0);
  const [c1x, c1y] = at(len / 2, width);
  const [c2x, c2y] = at(len / 2, -width);
  g.moveTo(x, y).quadraticCurveTo(c1x, c1y, tx, ty).quadraticCurveTo(c2x, c2y, x, y).closePath();
}

const svg = (g: Graphics, d: string, ink: number) => g.path(new GraphicsPath(d)).fill(ink);

const ACTION: Record<string, Draw> = {
  // A galley's hull under a square sail.
  seaport: (g, ink) => {
    svg(g, 'M-10 3.5 H10 L7 9.5 H-7 Z', ink);
    g.rect(-1.2, -10, 2.4, 13.5).fill(ink);
    svg(g, 'M2 -9 Q8.5 -4 2 2 Z M-2 -9 Q-8.5 -4 -2 2 Z', ink);
  },
  // A pediment on three columns.
  forum: (g, ink) => {
    svg(g, 'M0 -10.5 L11 -3.5 H-11 Z', ink);
    g.rect(-7.2, -2, 3.2, 9).fill(ink);
    g.rect(-1.6, -2, 3.2, 9).fill(ink);
    g.rect(4, -2, 3.2, 9).fill(ink);
    g.rect(-10, 8, 20, 2.4).fill(ink);
  },
  // A legion's shield.
  military: (g, ink) => {
    svg(g, 'M0 -10.5 L9.5 -7 V1 Q9.5 7.5 0 11 Q-9.5 7.5 -9.5 1 V-7 Z', ink);
  },
  // A rostrum: three steps and a standard.
  senate: (g, ink) => {
    g.rect(-1.4, -11, 2.8, 7).fill(ink);
    g.rect(-4.5, -4, 9, 3).fill(ink);
    g.rect(-7.5, 0.5, 15, 3).fill(ink);
    g.rect(-10.5, 5, 21, 3.4).fill(ink);
  },
  // The arch.
  trajan: (g, ink) => {
    svg(g, 'M-10 10.5 V-1 A10 10 0 0 1 10 -1 V10.5 H4.4 V-1 A4.4 4.4 0 0 0 -4.4 -1 V10.5 Z', ink);
  },
  // Coursed brickwork.
  construction: (g, ink) => {
    g.rect(-10.5, -9, 8.5, 5).fill(ink);
    g.rect(0.5, -9, 10, 5).fill(ink);
    g.rect(-10.5, -2, 12.5, 5).fill(ink);
    g.rect(4.5, -2, 6, 5).fill(ink);
    g.rect(-10.5, 5, 6.5, 5).fill(ink);
    g.rect(-1.5, 5, 12, 5).fill(ink);
  },
};

const DEMAND: Record<string, Draw> = {
  // A round loaf with its scoring.
  bread: (g, ink) => {
    g.ellipse(0, 1.5, 11, 7.5).fill(ink);
    g.moveTo(-6, -2).lineTo(-3, 5).moveTo(-1, -3.5).lineTo(2, 5.5).moveTo(4, -2.5).lineTo(6.5, 4);
    g.stroke({ width: 1.6, color: 0xffffff, alpha: 0.55, cap: 'round' });
  },
  // A gladiator's helmet: dome, brim and a grille.
  games: (g, ink) => {
    svg(g, 'M-9 5 Q-9.5 -9 0 -9.5 Q9.5 -9 9 5 L11 7 H-11 Z', ink);
    g.rect(-1, -12, 2, 4).fill(ink);
    g.moveTo(-5, -1).lineTo(5, -1).moveTo(-5, 2.5).lineTo(5, 2.5).moveTo(0, -4).lineTo(0, 5);
    g.stroke({ width: 1.3, color: 0xffffff, alpha: 0.55 });
  },
  // A flame on an altar.
  religion: (g, ink) => {
    svg(g, 'M0 -11 Q7.5 -3 5 2.5 Q3.5 5.5 0 5.5 Q-3.5 5.5 -5 2.5 Q-7.5 -3 0 -11 Z', ink);
    g.rect(-7.5, 6.5, 15, 2.6).fill(ink);
    g.rect(-5.5, 9.1, 11, 2.4).fill(ink);
  },
};
DEMAND.helmet = DEMAND.games!;
DEMAND.flame = DEMAND.religion!;

const BUILDING: Record<string, Draw> = {
  stair: (g, ink) => {
    svg(g, 'M-11 11 V5 H-5 V-1 H1 V-7 H7 V-11 H11 V11 Z', ink);
  },
  fountain: (g, ink) => {
    svg(g, 'M-11 5 H11 L8 11 H-8 Z', ink);
    g.rect(-1.5, -3, 3, 8).fill(ink);
    svg(g, 'M-6 -4 H6 L4 -1 H-4 Z', ink);
    g.moveTo(0, -5).quadraticCurveTo(-7, -12, -9, 3).moveTo(0, -5).quadraticCurveTo(7, -12, 9, 3);
    g.stroke({ width: 1.4, color: ink, cap: 'round' });
  },
  door: (g, ink) => {
    svg(g, 'M-7 11 V-3 A7 7 0 0 1 7 -3 V11 Z', ink);
    g.circle(3.5, 3, 1.2).fill(0xffffff);
    g.rect(-10, 10, 20, 1.8).fill(ink);
  },
  window: (g, ink) => {
    g.path(new GraphicsPath('M-7.5 9 V-2.5 A7.5 7.5 0 0 1 7.5 -2.5 V9 Z'));
    g.stroke({ width: 2.4, color: ink });
    g.moveTo(0, -10).lineTo(0, 9).moveTo(-7.5, 1).lineTo(7.5, 1);
    g.stroke({ width: 1.8, color: ink });
    g.rect(-10, 9.5, 20, 2).fill(ink);
  },
  rooftop: (g, ink) => {
    svg(g, 'M0 -10 L12 1 H-12 Z', ink);
    g.rect(-8, 2.5, 16, 8.5).fill(ink);
    g.moveTo(-6, -3).lineTo(6, -3).moveTo(-3, -6.5).lineTo(3, -6.5);
    g.stroke({ width: 1.2, color: 0xffffff, alpha: 0.5 });
  },
};

const COMMODITY: Record<string, Draw> = {
  wheat: (g, ink) => {
    g.moveTo(0, 11).lineTo(0, -8).stroke({ width: 1.6, color: ink, cap: 'round' });
    for (const y of [-6, -1.5, 3]) {
      leaf(g, 0, y + 2, 7, 2.2, -Math.PI / 2 - 0.55);
      leaf(g, 0, y + 2, 7, 2.2, -Math.PI / 2 + 0.55);
    }
    leaf(g, 0, -7, 5.5, 2, -Math.PI / 2);
    g.fill(ink);
  },
  fish: (g, ink) => {
    svg(g, 'M-9 0 Q-2 -8 6 -1 L11 -6 L10 0 L11 6 L6 1 Q-2 8 -9 0 Z', ink);
    g.circle(-5, -1, 1.2).fill(0xffffff);
  },
  fruit: (g, ink) => {
    g.circle(-3.2, 2.5, 6).fill(ink);
    g.circle(3.2, 2.5, 6).fill(ink);
    g.rect(-0.8, -8, 1.6, 5).fill(ink);
    leaf(g, 0.5, -6, 7, 2.5, -0.5);
    g.fill(ink);
  },
  paper: (g, ink) => {
    g.roundRect(-7, -8, 14, 16, 1).fill(ink);
    g.ellipse(0, -9, 8.5, 2.4).fill(ink);
    g.ellipse(0, 9, 8.5, 2.4).fill(ink);
    for (const y of [-4, -0.5, 3]) g.moveTo(-4.5, y).lineTo(4.5, y);
    g.stroke({ width: 1.2, color: 0xffffff, alpha: 0.6 });
  },
  jewelry: (g, ink) => {
    g.circle(0, 3.5, 6.5).stroke({ width: 2.6, color: ink });
    svg(g, 'M-4 -5 L-2 -9 H2 L4 -5 L0 -1.5 Z', ink);
  },
  necklace: (g, ink) => {
    for (let k = 0; k <= 8; k++) {
      const a = Math.PI * (0.05 + (0.9 * k) / 8);
      g.circle(Math.cos(a) * 9, -6 + Math.sin(a) * 9, 1.7);
    }
    g.fill(ink);
    svg(g, 'M0 3 Q4 7 0 11 Q-4 7 0 3 Z', ink);
  },
  leather: (g, ink) => {
    svg(
      g,
      'M-4 -9 L-9 -11 L-7 -5 Q-10 0 -7 5 L-9 11 L-4 8 Q0 10 4 8 L9 11 L7 5 Q10 0 7 -5 L9 -11 L4 -9 Q0 -11 -4 -9 Z',
      ink,
    );
  },
  pottery: (g, ink) => {
    svg(g, 'M-10 -4 H10 Q10 7 3 8.5 H-3 Q-10 7 -10 -4 Z', ink);
    g.rect(-11, -6.5, 22, 2.5).fill(ink);
    g.rect(-4, 8.5, 8, 2.5).fill(ink);
  },
  statue: (g, ink) => {
    g.circle(0, -6, 4.2).fill(ink);
    svg(g, 'M-8 6 Q-8 -1 0 -1 Q8 -1 8 6 Z', ink);
    g.rect(-6, 6.5, 12, 2.2).fill(ink);
    g.rect(-8.5, 9, 17, 2.4).fill(ink);
  },
  wine: (g, ink) => {
    const rows = [
      [-4.5, -1],
      [0, -1],
      [4.5, -1],
      [-2.25, 3],
      [2.25, 3],
      [0, 7],
    ];
    for (const [x, y] of rows) g.circle(x!, y!, 2.5);
    g.fill(ink);
    g.rect(-0.8, -9, 1.6, 6).fill(ink);
    leaf(g, 0.5, -6, 7, 2.6, -0.3);
    g.fill(ink);
  },
  cow: (g, ink) => {
    svg(g, 'M-6 -4 Q-6 -8 0 -8 Q6 -8 6 -4 L5 7 Q5 10 0 10 Q-5 10 -5 7 Z', ink);
    g.moveTo(-5.5, -6.5).quadraticCurveTo(-11, -7, -10, -12);
    g.moveTo(5.5, -6.5).quadraticCurveTo(11, -7, 10, -12);
    g.stroke({ width: 2, color: ink, cap: 'round' });
    leaf(g, -5.5, -3, 5, 1.8, Math.PI + 0.35);
    leaf(g, 5.5, -3, 5, 1.8, -0.35);
    g.fill(ink);
    g.circle(-2, 6.5, 1).circle(2, 6.5, 1).fill(0xffffff);
  },
  vase: (g, ink) => {
    svg(g, 'M-3 -11 H3 V-7 Q8 -4 7 3 Q6 9 2 11 H-2 Q-6 9 -7 3 Q-8 -4 -3 -7 Z', ink);
    g.moveTo(-3, -8).quadraticCurveTo(-9, -8, -7, -1).moveTo(3, -8).quadraticCurveTo(9, -8, 7, -1);
    g.stroke({ width: 1.6, color: ink });
  },
};

const MISC: Record<string, Draw> = {
  arch: ACTION.trajan!,
  star: (g, ink) => {
    g.star(0, 0, 5, 11, 4.6).fill(ink);
  },
  laurel: (g, ink) => {
    for (let k = 0; k < 5; k++) {
      const a = Math.PI * (0.62 + k * 0.17);
      const x = Math.cos(a) * 9;
      const y = Math.sin(a) * 9 - 1;
      leaf(g, x, y, 5.5, 1.9, a - Math.PI / 2 - 0.4);
      leaf(g, -x, y, 5.5, 1.9, Math.PI - (a - Math.PI / 2 - 0.4));
    }
    g.fill(ink);
  },
  cards: (g, ink) => {
    g.roundRect(-9, -7, 11, 15, 1.5).stroke({ width: 2, color: ink });
    g.roundRect(-2, -9, 11, 15, 1.5).fill(ink);
  },
  hammer: (g, ink) => {
    g.rect(-1.3, -4, 2.6, 15).fill(ink);
    svg(g, 'M-8 -10 H6 Q10 -8 9 -4 H-8 Z', ink);
  },
  spear: (g, ink) => {
    g.moveTo(-8, 10).lineTo(6, -4).stroke({ width: 2, color: ink, cap: 'round' });
    svg(g, 'M4 -2 L11 -11 L2 -4 Z', ink);
    g.moveTo(-6, 4).lineTo(-2, 8).stroke({ width: 2, color: ink });
  },
  camp: (g, ink) => {
    for (let k = -2; k <= 2; k++)
      svg(g, `M${k * 4.4 - 1.8} 10 V-4 L${k * 4.4} -8 L${k * 4.4 + 1.8} -4 V10 Z`, ink);
    g.rect(-11, -1, 22, 2).fill(ink);
  },
  wild: (g, ink) => {
    g.star(0, 0, 8, 11, 6).fill(ink);
    g.circle(0, 0, 4).fill(0xffffff);
  },
  hourglass: (g, ink) => {
    svg(g, 'M-7 -11 H7 V-9 Q7 -3 1.5 0 Q7 3 7 9 V11 H-7 V9 Q-7 3 -1.5 0 Q-7 -3 -7 -9 Z', ink);
  },
};

export type IconName = string;

const ALL: Record<string, Draw> = { ...ACTION, ...DEMAND, ...BUILDING, ...COMMODITY, ...MISC };

export const hasIcon = (name: string): boolean => name in ALL;

/** A new Graphics holding icon `name`, `size` units across, centred on its origin. */
export function icon(name: IconName, size: number, ink: number): Graphics {
  const g = new Graphics();
  (ALL[name] ?? MISC.star!)(g, ink);
  g.scale.set(size / 24);
  return g;
}
